import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { initTheme } from "./lib/theme";
import { AuthProvider, handleAuthFailure } from "./auth/AuthContext";
import { ToastProvider } from "./components/ui/Feedback";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { ApiError } from "./lib/api";
import { LocalProvider } from "./local/LocalProvider";
import { initInstallCapture } from "./pwa/install";
import "./index.css";

// The browser offers "install" once, early: catch it before any screen exists.
initInstallCapture();

function onAuthError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) handleAuthFailure(queryClient, error);
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // By default TanStack Query never even attempts a request while the browser reports
      // "offline" and just spins. Always attempt, so an unreachable server becomes an immediate,
      // explainable error (or an offline fallback) rather than an endless loading state.
      networkMode: "always",
      retry: (count, error) =>
        // A missing network or a 4xx will not fix itself in a second: fail fast.
        !(error instanceof ApiError && (error.status === 0 || (error.status >= 400 && error.status < 500))) &&
        count < 2,
    },
    mutations: { networkMode: "always" },
  },
});

// Before React mounts, and deliberately NOT an inline script in index.html:
// this app ships a strict CSP (script-src 'self', no unsafe-inline), so an
// inline block is blocked outright and the page silently keeps the light
// theme. Running it here costs the time it takes this module to evaluate,
// which is before the first paint of anything React renders.
initTheme();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
            <LocalProvider>
              <App />
              <UpdatePrompt />
            </LocalProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
